import {
  AiBuilderAbout,
  AiBuilderSystemMessage,
  AiBuilderTurnInput,
  AskAiBuilderResponse,
  flowStructureUtil,
  GenerateFlowRequest,
  FlowVersion,
  maskTypedSecrets,
} from '@activepieces/shared';
import { useMutation } from '@tanstack/react-query';
import { t } from 'i18next';
import { nanoid } from 'nanoid';
import { useEffect, useRef, useState } from 'react';

import { useBuilderStateContext } from '../builder-hooks';

import { generateFlow } from './ai-builder-turn-api';
import {
  AiBuilderChatMessage,
  isAnswerLost,
  needHelpOn,
  PendingTurnInput,
  pendingInputAfterTurn,
  replyToOf,
  toChatContext,
  turnAnswerMessages,
} from './ai-builder-utils';
import { useAiBuilderTurnEvents } from './use-ai-builder-turn-events';
import { useChatStore } from './useChatStore';

import { useSocket } from '@/components/socket-provider';
import { toast } from '@/components/ui/use-toast';

/**
 * The whole turn protocol of the AI builder chat, extracted from the
 * component so it is testable: sending (free text and protocol payloads),
 * the resume token rules, retry, the socket events, and the auto-resume
 * gates. The component keeps only DOM concerns (scroll, focus) and rendering.
 */
export const useAiBuilderTurn = (): {
  sendMessage: (text: string) => Promise<void>;
  sendSilentAction: (
    input: AiBuilderTurnInput,
    displayedChoice?: string,
    about?: AiBuilderAbout,
  ) => Promise<void>;
  /**
   * Replays a failed turn: truncates the failed message from the chat AND
   * re-sends its exact sourceInput (protocol payloads via the silent path).
   * Both gestures live here — they are one operation; the component keeps
   * only the Undo button, which is a chat edit, not a turn.
   */
  retryTurn: (message: AiBuilderChatMessage) => void;
  isGenerating: boolean;
  /** The last thing the user said never got its answer (page left mid-turn) */
  isAnswerLost: boolean;
  loadingMessage: string;
} => {
  const {
    flowVersion,
    applyAiPartialFlowElements,
    waitForSavesToSettle,
    triggerCanvasRefit,
    aiHelpRequest,
    clearAiHelpRequest,
    connectionCreatedNonce,
    selectedStep,
    userStepSelectionNonce,
  } = useBuilderStateContext((state) => ({
    flowVersion: state.flowVersion,
    applyAiPartialFlowElements: state.applyAiPartialFlowElements,
    waitForSavesToSettle: state.waitForSavesToSettle,
    triggerCanvasRefit: state.triggerCanvasRefit,
    aiHelpRequest: state.aiHelpRequest,
    clearAiHelpRequest: state.clearAiHelpRequest,
    connectionCreatedNonce: state.connectionCreatedNonce,
    selectedStep: state.selectedStep,
    userStepSelectionNonce: state.userStepSelectionNonce,
  }));

  const flowId = flowVersion.flowId;
  const {
    chats,
    turnsInFlight,
    addMessage,
    setMessages,
    startTurn,
    endTurn,
    setPendingInput,
    recordAnswerToApply,
    setAwaitingConnection,
    getOrCreateConversationId,
    takePendingAbout,
    setPendingAbout,
  } = useChatStore();
  // Not a selector: a fresh boolean per flow is cheap, and the effect below
  // needs the CURRENT value in its dependency array to react to a connection
  // created while this chat was unmounted
  const awaitingConnection = useChatStore(
    (state) => state.awaitingConnection[flowId] === true,
  );
  // The turn's own input, persisted until its answer is stored: survives a
  // reload, unlike `turnsInFlight` (session-only)
  const pendingInput = useChatStore((state) => state.pendingInput[flowId]);
  const messages = chats[flowId] || [];
  const socket = useSocket();

  // In the store, not in this component: a turn outlives the page that sent
  // it, and the chat reopened meanwhile must still show it running
  const isGenerating = turnsInFlight[flowId] === true;
  const [loadingMessage, setLoadingMessage] = useState<string>(
    t('Thinking...'),
  );
  const previousFlowVersionRef = useRef<FlowVersion | null>(null);
  // The input of the turn in flight, stored on its answer so retry can
  // replay exactly that turn
  const sentInputRef = useRef<AiBuilderTurnInput | undefined>(undefined);
  // This turn's own id: minted once per dispatch, carried on the request AND
  // read by useAiBuilderTurnEvents to tell its own mid-turn lines from a
  // previous turn's (an abandoned retry, another tab on this flow)
  const requestIdRef = useRef<string | undefined>(undefined);
  // What was waiting for an answer when this turn was sent: a busy refusal
  // gives it back (`pendingInputAfterTurn`)
  const pendingBeforeSendRef = useRef<PendingTurnInput | undefined>(undefined);

  // Read fresh every render, without being a dependency below: the effect
  // must fire on a step selection only, and reacting to isGenerating would
  // re-set the pending "about" the instant a turn ends
  const isGeneratingRef = useRef(isGenerating);
  useEffect(() => {
    isGeneratingRef.current = isGenerating;
  });

  // Clicking a step while this chat is open sets what the next message is
  // about. `userStepSelectionNonce` is bumped once per user click, so a
  // re-click counts and a selection made by code does not; the ref makes
  // mount a no-op, so a remounted sidebar never re-adds a consumed "About".
  // A selection driven mid-turn is not the user asking about something new.
  const processedStepSelectionNonceRef = useRef(userStepSelectionNonce);
  useEffect(() => {
    if (processedStepSelectionNonceRef.current === userStepSelectionNonce) {
      return;
    }
    processedStepSelectionNonceRef.current = userStepSelectionNonce;
    if (isGeneratingRef.current || !selectedStep) return;
    const step = flowStructureUtil.getStep(selectedStep, flowVersion);
    if (!step) return;
    setPendingAbout(flowId, {
      about: { type: 'step', stepName: selectedStep },
      label: t('About: {step}', { step: step.displayName }),
    });
    // The nonce check above is the gate: the other dependencies re-run this
    // effect harmlessly into its early return
  }, [
    userStepSelectionNonce,
    selectedStep,
    flowVersion,
    flowId,
    setPendingAbout,
  ]);

  // The creation name is written TRANSLATED by the client, so the server
  // cannot recognise it: only here can we say "still unnamed"
  const isUnnamedFlow = flowVersion.displayName === t('Untitled');

  const { mutate } = useMutation({
    mutationFn: (params: GenerateFlowRequest & { requestId: string }) => {
      previousFlowVersionRef.current = structuredClone(flowVersion);
      sentInputRef.current = params.input;
      return generateFlow(params, socket);
    },
    onSuccess: (response: AskAiBuilderResponse) => {
      const botMessage = turnAnswerMessages(response, {
        previousFlowVersion: previousFlowVersionRef.current,
        sourceInput: sentInputRef.current,
      });
      addMessage(flowId, botMessage);
      // Applying it to the canvas is the page-level owner's job
      // (useApplyAiAnswers): this only hands the answer off.
      recordAnswerToApply(flowId, botMessage);
    },
    onError: (error, params) => {
      // The turn is lost (client timeout, socket dropped): the toast is gone as
      // soon as the user navigates, so the chat must keep the trace itself —
      // otherwise the conversation looks silently dead on the next visit.
      addMessage(flowId, {
        body: {
          kind: 'key',
          key: AiBuilderSystemMessage.turnFailed,
          params: {},
        },
        userType: 'bot',
        // What retry replays — this is the one case the button is for
        sourceInput: sentInputRef.current,
        // As sent: the store's pending "about" was already consumed and the
        // reply chip may have moved on by the time the user clicks Retry
        sourceAbout: params.about,
        sourceReplyTo: params.replyTo,
      });
      toast({
        variant: 'destructive',
        title: t('Error generating flow'),
        description: error.message,
      });
    },
    onSettled: (response, _error, params) => {
      endTurn(params.flowId);
      // An answer or a definitive failure is now in the chat
      setPendingInput(
        params.flowId,
        pendingInputAfterTurn(response, pendingBeforeSendRef.current)?.input,
      );
    },
  });

  /**
   * Sends one turn against an EXPLICIT history. Callers pass the conversation
   * they just wrote rather than letting this read `messages`: within a turn
   * that closure still holds the state from before their own setMessages.
   * `about` is attached ONLY by `sendMessage`: a silent protocol payload is
   * never what the "Understand this error" pointer was about.
   */
  const dispatchTurn = async (
    input: AiBuilderTurnInput,
    history: AiBuilderChatMessage[],
    about?: AiBuilderAbout,
    // What the reply chip was doing when this message was sent: only a
    // message the user typed confirms or dismisses the chip
    replyTo?: GenerateFlowRequest['replyTo'],
  ) => {
    startTurn(flowId);
    pendingBeforeSendRef.current = pendingInput;
    // Set BEFORE the send: a reload mid-`waitForSavesToSettle` must already
    // read this turn as lost
    setPendingInput(flowId, input);
    // Set BEFORE the send too: a mid-turn event can arrive before `mutate`'s
    // own emit resolves this tick
    requestIdRef.current = nanoid();
    await waitForSavesToSettle();
    mutate({
      input,
      context: toChatContext(history),
      flowId: flowVersion.flowId,
      requestId: requestIdRef.current,
      // The paused-build pointer is the server's alone to keep, keyed by
      // conversationId: the client never echoes one back
      flowIsUnnamed: isUnnamedFlow,
      conversationId: getOrCreateConversationId(flowId),
      about,
      replyTo,
    });
  };

  const sendMessage = async (typed: string) => {
    // A secret typed here would be stored, replayed to the model on every turn
    // and copied into the flow: only its mask is kept and sent
    const text = maskTypedSecrets(typed);
    if (text !== typed) {
      toast({
        title: t('Secret removed from your message'),
        description: t(
          'Paste it into the field of the step, in the panel: everything typed in the chat is kept in the conversation.',
        ),
      });
    }
    const userMessage: AiBuilderChatMessage = {
      body: { kind: 'text', text },
      userType: 'user',
    };

    addMessage(flowId, userMessage);
    // Attached once: the "Understand this error" pointer answers THIS message,
    // never silently reused for whatever the user types next
    await dispatchTurn(
      { type: 'text', text },
      [...messages, userMessage],
      takePendingAbout(flowId)?.about,
      // Read BEFORE userMessage is appended: the chip's state is what it was
      // when the user decided to send, not "answered" by their own new line
      replyToOf(messages),
    );
  };

  /**
   * Sends a typed input (step_done, need_help, a card reply…) the user never
   * types. `displayedChoice` is what they actually picked — a button label,
   * an option — and it stays in the conversation: without it the select and
   * the buttons vanish with the next message and nobody can tell what was
   * answered. `about` is the ONE exception to "about is sendMessage's alone":
   * the panel's "Need Help?" names its step the same way a typed message
   * would, but is itself silent.
   */
  const sendSilentAction = async (
    input: AiBuilderTurnInput,
    displayedChoice?: string,
    about?: AiBuilderAbout,
  ) => {
    const answered = displayedChoice
      ? messages.map((message, index) =>
          index === messages.length - 1 && message.userType === 'bot'
            ? { ...message, answeredChoice: displayedChoice }
            : message,
        )
      : messages;
    if (displayedChoice) {
      // The choice replaces the select/buttons under the very message it
      // answers — a validated pick, not a line the user typed
      setMessages(flowId, answered);
    }
    // Panel edits (e.g. the dropdown the wizard just asked for) are autosaved
    // asynchronously: sending before they settle makes the server re-read a
    // stale version and ask the same question again — dispatchTurn waits.
    await dispatchTurn(input, answered, about);
  };

  const retryTurn = (message: AiBuilderChatMessage): void => {
    // Replays THIS turn: its own input, typed or not. Anything else re-ran
    // an unrelated request — on a finished flow, the original build one.
    const input = message.sourceInput;
    if (!input) return;
    // Drops the failed answer and replays the turn as it was first sent — its
    // own line is already in the history, whether the user typed it or it was
    // a button nobody displayed. Re-adding it would print the prompt twice
    // and hand the model a duplicated user turn.
    const withoutFailedAnswer = messages.slice(0, -1);
    setMessages(flowId, withoutFailedAnswer);
    // The failed message is the only place still carrying what the first
    // attempt sent: the store's own state has moved on by now
    void dispatchTurn(
      input,
      withoutFailedAnswer,
      message.sourceAbout,
      message.sourceReplyTo,
    );
  };

  // Handle "Need Help?" button from step settings. The ref guards the render
  // between the send and the store clearing the request; once cleared, the
  // same step can ask again. A request made mid-turn waits for the turn to end.
  const helpRequestSentRef = useRef<string | null>(null);
  useEffect(() => {
    if (aiHelpRequest === null) {
      helpRequestSentRef.current = null;
      return;
    }
    if (isGenerating || helpRequestSentRef.current === aiHelpRequest) return;
    helpRequestSentRef.current = aiHelpRequest;
    const { input, about } = needHelpOn(aiHelpRequest);
    void sendSilentAction(input, undefined, about);
    clearAiHelpRequest();
    // sendSilentAction/clearAiHelpRequest are stable store actions: only a
    // genuinely new help request (or the turn ending) should re-run this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiHelpRequest, isGenerating]);

  // Auto-resume the wizard once the connection asked by a connect-account
  // gate is created — no "done" click needed. Reads the store, not a local
  // ref: the gate may have been armed by the page-level owner while this
  // chat was unmounted, and a remount must still see it.
  useEffect(() => {
    if (connectionCreatedNonce > 0 && awaitingConnection && !isGenerating) {
      setAwaitingConnection(flowId, false);
      void sendSilentAction({ type: 'step_done' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionCreatedNonce, awaitingConnection]);

  useAiBuilderTurnEvents({
    socket,
    flowId,
    requestIdRef,
    setLoadingMessage,
    applyAiPartialFlowElements,
    triggerCanvasRefit,
    addMessage,
  });

  useEffect(() => {
    const currentMessages = useChatStore.getState().chats[flowId];
    if (
      flowId &&
      (!currentMessages || currentMessages.length === 0) &&
      !helpRequestSentRef.current
    ) {
      addMessage(flowId, {
        body: {
          kind: 'text',
          text: `${t('Welcome to AI Builder!')}\n${t(
            "Could you please describe the desired flow or any modifications you'd like to implement?",
          )}`,
        },
        userType: 'bot',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId, addMessage, t]);

  return {
    sendMessage,
    sendSilentAction,
    retryTurn,
    isGenerating,
    isAnswerLost: isAnswerLost(pendingInput, isGenerating),
    loadingMessage,
  };
};
